import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useModal } from './ModalContext'; // Assumes ModalContext provides a confirmation modal

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNavigationConfirm } = useModal();

  // --- Navigation Guard State ---
  // Initialize guard as active if we're on a "new" route (handles page reloads)
  const [guard, setGuard] = useState(() => {
    const isOnNewRoute = location.pathname === '/conversations/new' || location.pathname === '/channels/new';
    return {
      isActive: isOnNewRoute,
      title: 'Unsaved Changes',
      message: 'Are you sure you want to leave? Your changes will be lost.',
      onConfirm: () => {},
      confirmText: 'Leave',
      cancelText: 'Stay',
    };
  });
  // Use a ref to hold the most current guard state to avoid race conditions.
  const guardRef = useRef(guard);
  guardRef.current = guard;

  /**
   * Allows components to enable or disable the navigation guard.
   * @param {object} options - { isActive, title, message, confirmText, cancelText, onConfirm }
   */
  const setNavigationGuard = useCallback((options) => {
    setGuard(prev => ({ ...prev, ...options }));
  }, []);

  // --- Core Navigation Logic ---

  /**
   * A private, guarded navigation function.
   * Checks for an active guard before navigating.
   */
  const guardedNavigate = useCallback((destination, options = {}) => {
    const currentGuard = guardRef.current;

    if (currentGuard.isActive && !options.bypassGuard) {
      showNavigationConfirm({
        title: currentGuard.title,
        message: currentGuard.message,
        confirmText: currentGuard.confirmText,
        cancelText: currentGuard.cancelText,
        onConfirm: () => {
          currentGuard.onConfirm?.(); // Execute the guard's onConfirm callback
          setGuard(prev => ({ ...prev, isActive: false })); // Disarm the guard
          navigate(destination);
        },
        onCancel: null, // Default behavior is to just close the modal
      });
    } else {
      navigate(destination);
    }
  }, [navigate, showNavigationConfirm]);

  // --- Public Navigation API ---

  const navigateToConversation = useCallback((id = null, options = {}) => {
    if (id) {
      const encodedId = typeof id === 'string' ? encodeURIComponent(id) : id;
      guardedNavigate(`/conversations/${encodedId}`, options);
    } else {
      guardedNavigate('/conversations', options);
    }
  }, [guardedNavigate]);

  const navigateToContact = useCallback((id = null, options = {}) => {
    if (id) {
      const encodedId = typeof id === 'string' ? encodeURIComponent(id) : id;
      guardedNavigate(`/contacts/${encodedId}`, options);
    } else {
      guardedNavigate('/contacts', options);
    }
  }, [guardedNavigate]);

  const navigateToChannel = useCallback((id = null, options = {}) => {
    if (id) {
      const encodedId = typeof id === 'string' ? encodeURIComponent(id) : id;
      guardedNavigate(`/channels/${encodedId}`, options);
    } else {
      guardedNavigate('/channels', options);
    }
  }, [guardedNavigate]);

  // --- Browser Refresh/Close Prevention ---
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Use the ref to get the most current guard state.
      if (guardRef.current.isActive) {
        // Standard way to trigger the browser's native confirmation dialog.
        event.preventDefault();
        // For older browsers, setting returnValue is necessary.
        event.returnValue = guardRef.current.message;
        return guardRef.current.message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array ensures this runs only once.

  // --- Route State & Utilities ---

  const currentPage = location.pathname.split('/')[1] || null;
  // Check for the exact "new conversation" or "new channel" route.
  const isCreatingNew = location.pathname === '/conversations/new' || location.pathname === '/channels/new';

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Navigation Methods
    navigateToConversation,
    navigateToContact,
    navigateToChannel,

    // Guard Control
    setNavigationGuard,

    // Route State
    currentPage,
    isCreatingNew,
    location,
  }), [
    navigateToConversation,
    navigateToContact,
    navigateToChannel,
    setNavigationGuard,
    currentPage,
    isCreatingNew,
    location,
  ]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

NavigationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
